import React from 'react';
import { styled } from '@linaria/react';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Paper from '@/components/Paper';
import Paragraph from '@/components/Paragraph';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import ArrowShowingDistance from '@/components/ArrowShowingDistance';

import { CODE_BACKGROUND } from './index.constants';
import { constructSnippet } from './OffCenter.helpers';

const MODAL_HEIGHT = 96;

interface Props {
  initialNudge: number;
  codeOutput: React.ReactNode;
  variant: 'initial' | 'with-boundaries';
}

function OffCenter({ codeOutput, variant, initialNudge }: Props) {
  return (
    <Demo
      initialValues={{
        nudge: initialNudge,
      }}
      controlPosition="top"
      customGrid={{ gridTemplateColumns: '1fr 1fr' }}
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          key="nudge"
          id="nudge"
          label="Offset"
          min={variant === 'initial' ? 0 : 20}
          max={variant === 'initial' ? 48 : 64}
          previewRenderer={(val) => `${val}px`}
        />,
      ]}
      renderAfter={({ nudge }) => {
        if (variant === 'initial') {
          return null;
        }
        return (
          <Paragraph>
            In other words, the element is suspended between the top
            and bottom edges of an imaginary box, but now that box
            sits {nudge}px away from the bottom edge of its container.
          </Paragraph>
        );
      }}
    >
      {({ nudge }) => {
        return (
          <>
            <Row>
              <Column>
                <CodeSnippetProvider
                  lang="css"
                  code={constructSnippet(nudge)}
                >
                  {codeOutput}
                </CodeSnippetProvider>
              </Column>
              <DemoColumn>
                <Container>
                  <Background>
                    <Heading>
                      {[2, 6.5, 1, 8, 3].map((width, index) => (
                        <HeadingBlock
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </Heading>
                    <FakeParagraph>
                      {[
                        2, 1, 2.25, 1, 1.5, 4.75, 1.5, 7, 4, 4.2,
                        1.25, 5, 0.5, 2.1, 4, 0.785, 2.5, 1.5, 0.5,
                        3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeParagraph>
                    <FakeParagraph>
                      {[
                        0.785, 4.2, 1.25, 4, 1.5, 1.5, 1, 2.25, 0.5,
                        2.1, 2.5, 3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeParagraph>
                    <FakeParagraph>
                      {[
                        6, 2.25, 4, 1.3, 4.2, 8, 0.5, 1.25, 2.5, 5, 1,
                        4.4, 0.5, 8, 2.1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeParagraph>
                  </Background>
                  {variant === 'initial' ? (
                    <>
                      <ModalCenterIndicator />
                      <Modal style={{ bottom: nudge + 'px' }} />
                    </>
                  ) : (
                    <WithArrow>
                      <CenteringBox style={{ height: 256 - nudge }}>
                        <MeasuredModal />
                      </CenteringBox>

                      <ArrowWrapper>
                        <ArrowShowingDistance
                          animateTransitions={false}
                          axis="vertical"
                          distance={nudge}
                          unit="px"
                        />
                      </ArrowWrapper>
                    </WithArrow>
                  )}
                </Container>
              </DemoColumn>
            </Row>
          </>
        );
      }}
    </Demo>
  );
}

const Row = styled.div`
  display: flex;
  gap: 24px;
  padding-bottom: 16px;

  @media (max-width: 34rem) {
    flex-direction: column-reverse;
  }
`;
const Column = styled.div`
  position: relative;
  flex: 1;
  background: ${CODE_BACKGROUND};
  border-radius: 8px;
  height: 256px;
  max-height: 256px;
`;

const DemoColumn = styled(Column)`
  display: flex;
  justify-content: center;
`;

const Container = styled(Paper)`
  position: relative;
  background: hsl(0deg 0% 90%);
  color-scheme: light;
`;

const Background = styled.div`
  overflow: hidden;
  overflow: clip;
  padding: 16px;
  max-height: 100%;
`;

const Heading = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1.5em;
`;
const FakeParagraph = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 1em;
`;

const Block = styled.span`
  background: hsl(0deg 0% 80%);
  border-radius: 2px;
  height: 0.75rem;
`;
const HeadingBlock = styled(Block)`
  height: 1rem;
  background: hsl(0deg 0% 70%);
`;

const WithArrow = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: auto;
  overflow: hidden;
`;

const CenteringBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: auto;
  background: hsl(30deg 100% 50% / 0.2);
  border: 3px solid hsl(30deg 100% 50%);
  border-radius: 4px;
`;

const ModalBase = styled.div`
  position: absolute;
  inset: 0;
  width: 200px;
  height: ${MODAL_HEIGHT}px;
  margin: auto;
  border-radius: 4px;
`;

const ModalCenterIndicator = styled(ModalBase)`
  border: 3px dashed hsl(350deg 100% 50%);
  background-color: hsl(350deg 100% 50% / 0.2);
`;

const Modal = styled(ModalBase)`
  background-color: white;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.2),
    0px 2px 4px hsl(0deg 0% 0% / 0.2),
    0px 4px 8px hsl(0deg 0% 0% / 0.2);
`;

const MeasuredModal = styled(Modal)`
  position: relative;
  margin-inline: auto;
`;

const ArrowWrapper = styled.div`
  transition: opacity 300ms;
`;

export default React.memo(OffCenter);
